import { z } from 'zod';

import { getTranslation } from '../../../../utils/get-translation.util';
import { MERGE_FIELD_ISSUE_CODES } from '../constants/merge-field-issue.constants';
import { GRANTEE, TEMPLATE_SIGNATORY_TYPES } from '../constants/template-signatory-type.constant';
import { TemplateFieldType, VariableMappingType } from '../enums/mapping-editor.const';
import {
  ExpirationPeriod,
  SigningMode,
  TemplateCategory,
  TemplateSubCategory,
} from '../enums/template.const';
import { hasFile } from '../utils/has-file.util';

export const ADD_TEMPLATE_STEP_THREE_MAX_FILE_BYTES = 10 * 1024 * 1024;

export const stepThreeTabSchema = z.object({
  type: z.enum(TemplateFieldType).optional(),
  roleName: z.enum(TEMPLATE_SIGNATORY_TYPES).optional(),
  fieldKey: z.string().optional(),
  anchorString: z.string().min(1),
  value: z.string().optional(),
  pageNumber: z.number().optional(),
  xPosition: z.number().optional(),
  yPosition: z.number().optional(),
});
export type StepThreeTab = z.infer<typeof stepThreeTabSchema>;

export const stepOneShape = z.object({
  name: z
    .string()
    .trim()
    .min(1, getTranslation('DOCUMENT_NAME_REQUIRED'))
    .max(50, getTranslation('DOCUMENT_NAME_EXCEEDS_50')),
  category: z.enum(TemplateCategory).optional(),
  subCategory: z.enum(TemplateSubCategory).optional(),
  equityPlan: z.string().optional(),
  expireDocument: z.boolean(),
  expirationPeriod: z.enum(ExpirationPeriod).optional(),
});

export const stepOneSuperRefine = (
  data: Partial<z.infer<typeof stepOneShape>>,
  ctx: z.RefinementCtx,
) => {
  if (data.category !== undefined && data.subCategory === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('SUB_CATEGORY_REQUIRED'),
      path: ['subCategory'],
    });
  }
  if (data.expireDocument === true && data.expirationPeriod === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('EXPIRATION_PERIOD_REQUIRED'),
      path: ['expirationPeriod'],
    });
  }
  if (data.category === TemplateCategory.GRANT && (data.equityPlan?.trim?.() ?? '').length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('EQUITY_PLAN_REQUIRED_GRANT'),
      path: ['equityPlan'],
    });
  }
};

export const stepOneSchema = stepOneShape.superRefine((data, ctx) => {
  if (data.category === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('CATEGORY_REQUIRED'),
      path: ['category'],
    });
  }
  if (data.subCategory === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('SUB_CATEGORY_REQUIRED'),
      path: ['subCategory'],
    });
  }
  stepOneSuperRefine(data, ctx);
});

export type StepOneFormSchema = z.infer<typeof stepOneSchema>;

export const stepTwoShape = z.object({
  signatories: z
    .array(z.enum(TEMPLATE_SIGNATORY_TYPES))
    .min(1, getTranslation('AT_LEAST_ONE_SIGNATORY'))
    .max(5, getTranslation('MAX_5_SIGNATORIES')),
  signingMode: z.enum(SigningMode).optional(),
  signingOrderMatters: z.boolean(),
});

export const stepTwoSuperRefine = (
  data: Partial<z.infer<typeof stepTwoShape>>,
  ctx: z.RefinementCtx,
) => {
  const signatories = data.signatories ?? [];
  if (signatories.length > 0 && data.signingMode == null) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('SIGNING_MODE_REQUIRED'),
      path: ['signingMode'],
    });
  }
  if (data.signatories !== undefined && signatories.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('AT_LEAST_ONE_SIGNATORY'),
      path: ['signatories'],
    });
  }
};

export const stepTwoSchema = stepTwoShape.superRefine(stepTwoSuperRefine);

export const grantTemplateSignatoriesRefine = (
  data: {
    stepOne?: { category?: TemplateCategory };
    stepTwo?: { signatories?: z.infer<typeof stepTwoShape>['signatories'] };
  },
  ctx: z.RefinementCtx,
) => {
  if (data.stepOne?.category !== TemplateCategory.GRANT) return;
  const signatories = data.stepTwo?.signatories ?? [];
  if (!signatories.includes(GRANTEE)) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('GRANT_TEMPLATE_MUST_INCLUDE_GRANTEE'),
      path: ['stepTwo', 'signatories'],
    });
    return;
  }
  const hasOtherThanGrantee = signatories.some((s) => s !== GRANTEE);
  if (!hasOtherThanGrantee) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('GRANT_TEMPLATE_GRANTEE_AND_OTHER_SIGNATORY'),
      path: ['stepTwo', 'signatories'],
    });
  }
};

export const addTemplateFormGranteeRefine = (
  data: z.infer<typeof addTemplateFormSchema>,
  ctx: z.RefinementCtx,
) => {
  grantTemplateSignatoriesRefine(data, ctx);
};

export type StepTwoFormSchema = z.infer<typeof stepTwoSchema>;

const uploadedFileSchema = z.object({
  doc: z.instanceof(File),
  docLink: z.string(),
  loadProgress: z.number().optional(),
  abort: z.function().optional(),
});

const stepThreeFileField = z
  .union([z.instanceof(File, { message: getTranslation('FILE_REQUIRED') }), uploadedFileSchema])
  .refine((file) => {
    const f = file instanceof File ? file : file.doc;
    return f.size <= ADD_TEMPLATE_STEP_THREE_MAX_FILE_BYTES;
  }, getTranslation('FILE_SIZE_LESS_10_MB'))
  .refine((file) => {
    const f = file instanceof File ? file : file.doc;
    return f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }, getTranslation('ONLY_DOCX_ALLOWED'));

const mergeFieldIssueSchema = z.object({
  code: z.enum(MERGE_FIELD_ISSUE_CODES),
  field: z.string(),
});

export const stepThreeShape = z.object({
  file: stepThreeFileField.optional(),
  fieldMapping: z.record(z.string(), z.string()).default({}),
  fieldMappingTypes: z.record(z.string(), z.enum(VariableMappingType)),
  detectedVariables: z.array(z.string()).default([]),
  tabs: z.array(stepThreeTabSchema).default([]),
  fileParsed: z.boolean().default(false),
  mergeFieldIssues: z.array(mergeFieldIssueSchema).default([]),
});

export const stepThreeSuperRefine = (
  data: Partial<z.infer<typeof stepThreeShape>>,
  ctx: z.RefinementCtx,
) => {
  if (!hasFile(data.file)) return;
  if (data.fileParsed === false) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('OPEN_MAP_FIELDS_TO_CONTINUE'),
      path: ['fileParsed'],
    });
  }
  const detected = data.detectedVariables ?? [];
  if (detected.length > 0) {
    const mapping = data.fieldMapping ?? {};
    const allMapped = detected.every((v) => (mapping[v]?.trim?.() ?? '') !== '');
    if (!allMapped) {
      ctx.addIssue({
        code: 'custom',
        message: getTranslation('ALL_FIELDS_MUST_BE_MAPPED'),
        path: ['fieldMapping'],
      });
    }
  }
  const f = data.file;
  if (f != null && typeof f === 'object' && 'docLink' in f) {
    const docLinkTrim = f.docLink?.trim?.() ?? '';
    if (docLinkTrim === '') {
      const uploadPayload = f as { abort?: () => void; loadProgress?: number };
      const s3UploadStillInProgress =
        typeof uploadPayload.abort === 'function' ||
        (uploadPayload.loadProgress !== undefined && uploadPayload.loadProgress < 100);
      if (!s3UploadStillInProgress) {
        ctx.addIssue({
          code: 'custom',
          message: getTranslation('FILE_REQUIRED'),
          path: ['file'],
        });
      }
    }
  }
  const mergeIssues = data.mergeFieldIssues ?? [];
  if (mergeIssues.length > 0) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('MERGE_FIELD_ISSUES_BLOCK_SUBMIT'),
      path: ['mergeFieldIssues'],
    });
  }

  const tabs = data.tabs ?? [];
  for (let index = 0; index < tabs.length; index += 1) {
    const tab = tabs[index];
    if (tab.type == null) {
      ctx.addIssue({
        code: 'custom',
        message: getTranslation('SIGNATURE_FIELD_TYPE_REQUIRED'),
        path: ['tabs', index, 'type'],
      });
    }
    if (tab.roleName == null) {
      ctx.addIssue({
        code: 'custom',
        message: getTranslation('SIGNATURE_STAKEHOLDER_REQUIRED'),
        path: ['tabs', index, 'roleName'],
      });
    }
  }
};

export const stepThreeSchema = stepThreeShape.superRefine(stepThreeSuperRefine);

export type StepThreeFormSchema = z.infer<typeof stepThreeSchema>;

export type StepThreeFileValue = File | z.infer<typeof uploadedFileSchema>;

export const getStepThreeFile = (value: StepThreeFileValue | undefined) => {
  if (!value) return undefined;
  return value instanceof File ? value : value.doc;
};

export const getStepThreeFileLink = (value: StepThreeFileValue | undefined): string | undefined => {
  if (!value || typeof value !== 'object' || !('docLink' in value)) return undefined;
  const link = value.docLink;
  return link?.trim() ? link : undefined;
};

export const addTemplateFormSchema = z
  .object({
    stepOne: stepOneSchema,
    stepTwo: stepTwoSchema,
    stepThree: stepThreeSchema,
  })
  .superRefine(addTemplateFormGranteeRefine);

export type AddTemplateFormSchema = z.infer<typeof addTemplateFormSchema>;

export const getAddTemplateSchemaForStep = (step: number) => {
  if (step === 4) return addTemplateFormSchema;
  if (step === 2) {
    return z
      .object({
        stepOne: stepOneSchema,
        stepTwo: stepTwoSchema,
        stepThree: z.any(),
      })
      .superRefine(addTemplateFormGranteeRefine);
  }
  return z.object({
    stepOne: step === 1 ? stepOneSchema : z.any(),
    stepTwo: step === 2 ? stepTwoSchema : z.any(),
    stepThree: step === 3 ? stepThreeSchema : z.any(),
  });
};
